<template>
  <div class="place-order-page">
    <AppNavbar />

    <main class="container place-order-content">
      <!-- PROGRESS BAR -->
      <div class="checkout-progress">
        <div class="progress-step completed">
          <span class="step-dot"><i class="ri-shopping-cart-2-line"></i></span>
          <span class="step-label">Cart</span>
        </div>
        <div class="progress-line active"></div>
        <div class="progress-step active">
          <span class="step-dot"><i class="ri-map-pin-line"></i></span>
          <span class="step-label">Address</span>
        </div>
        <div class="progress-line"></div>
        <div class="progress-step">
          <span class="step-dot"><i class="ri-bank-card-line"></i></span>
          <span class="step-label">Payment</span>
        </div>
      </div>

      <p v-if="isLoading" class="status-text centered">
        Loading checkout details...
      </p>
      <p v-if="!isLoading && errorMessage" class="status-text error centered">
        {{ errorMessage }}
      </p>

      <section v-if="!isLoading" class="checkout-grid">
        <!-- LEFT: ADDRESS -->
        <section class="left-col">
          <div class="col-header">
            <h2 class="col-title">Delivery Address</h2>
            <div class="shipping-header-actions">
              <button
                v-if="hasOtherShippingAddresses"
                type="button"
                class="more-addr-btn"
                @click="toggleShippingAddressList"
              >
                <i
                  :class="
                    showAllShippingAddresses ? 'ri-eye-off-line' : 'ri-eye-line'
                  "
                ></i>
                {{
                  showAllShippingAddresses ? "Hide addresses" : "Show addresses"
                }}
              </button>
              <button
                type="button"
                class="add-addr-btn"
                @click="openAddressForm('shipping')"
              >
                <i class="ri-add-line"></i> Add New
              </button>
            </div>
          </div>

          <p v-if="isAddressLoading" class="status-text">
            Loading addresses...
          </p>

          <!-- ADD / EDIT ADDRESS FORM -->
          <AddressFormCard
            :show="showAddressForm && addressFormMode === 'shipping'"
            :form="addressForm"
            :address-types="addressTypes"
            :error-message="addressError"
            :type-warning-message="addressTypeWarning"
            :pincode-warning-message="pincodeWarningMessage"
            :is-editing="editingAddressId !== null"
            :mode-label="addressFormModeLabel"
            @update-field="updateAddressField"
            @cancel="cancelAddressForm"
            @submit="addAddress"
          />

          <!-- ADDRESS LIST -->
          <div class="address-list">
            <article
              v-for="address in visibleShippingAddresses"
              :key="address.id"
              class="address-card"
              :class="{ selected: selectedAddressId === address.id }"
              @click="selectAddress(address.id)"
            >
              <!-- TOP ROW: type + default pill left, edit/delete right -->
              <div class="addr-card-top">
                <div class="addr-left-meta">
                  <div class="addr-type-badge">
                    <i :class="getAddressTypeIcon(address.addressType)"></i>
                    {{ address.label || address.addressType || "Home" }}
                  </div>
                  <span v-if="address.isDefault" class="default-pill"
                    >Default</span
                  >
                </div>
                <div class="addr-right-actions">
                  <button
                    type="button"
                    class="icon-action-btn"
                    @click.stop="startEditAddress(address.id, 'shipping')"
                    title="Edit Address"
                  >
                    <i class="ri-edit-line"></i>
                  </button>
                  <button
                    type="button"
                    class="icon-action-btn danger"
                    @click.stop="removeAddress(address.id, 'shipping')"
                    title="Delete Address"
                  >
                    <i class="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>

              <!-- BODY: radio ring + address text -->
              <div class="addr-body">
                <div class="addr-check-col">
                  <div
                    class="radio-ring"
                    :class="{ active: selectedAddressId === address.id }"
                  >
                    <i
                      v-if="selectedAddressId === address.id"
                      class="ri-check-line check-icon"
                    ></i>
                  </div>
                </div>
                <div class="addr-text-col">
                  <p class="address-name">
                    {{ address.fullName || "Address" }}
                  </p>
                  <p class="address-line">
                    {{ address.addressLine || "Address line" }}
                  </p>
                  <p class="address-meta">
                    {{
                      [address.city, address.state, address.pincode]
                        .filter(Boolean)
                        .join(", ")
                    }}
                  </p>
                  <button
                    v-if="!address.isDefault"
                    type="button"
                    class="set-default-btn"
                    @click.stop="setDefaultAddress(address)"
                  >
                    Set as Default
                  </button>
                </div>
              </div>
            </article>
          </div>

          <div class="billing-section">
            <div class="col-header">
              <h3 class="billing-title">Billing Address</h3>
              <div class="shipping-header-actions">
                <button
                  v-if="billingMode === 'different' && hasOtherBillingAddresses"
                  type="button"
                  class="more-addr-btn"
                  @click="toggleBillingAddressList"
                >
                  <i
                    :class="
                      showAllBillingAddresses
                        ? 'ri-eye-off-line'
                        : 'ri-eye-line'
                    "
                  ></i>
                  {{
                    showAllBillingAddresses
                      ? "Hide addresses"
                      : "Show addresses"
                  }}
                </button>
                <button
                  v-if="billingMode === 'different'"
                  type="button"
                  class="add-addr-btn"
                  style="margin-bottom: 10px"
                  @click="openAddressForm('billing')"
                >
                  <i class="ri-add-line"></i> Add Billing
                </button>
              </div>
            </div>

            <div class="billing-mode-group">
              <label
                class="billing-mode-card"
                :class="{ selected: billingMode === 'same' }"
              >
                <input
                  v-model="billingMode"
                  type="radio"
                  name="billing-mode"
                  value="same"
                />
                <div
                  class="radio-ring"
                  :class="{ active: billingMode === 'same' }"
                >
                  <i
                    v-if="billingMode === 'same'"
                    class="ri-check-line check-icon"
                  ></i>
                </div>
                <div class="mode-text">
                  <p class="option-title">Same as delivery address</p>
                </div>
              </label>

              <label
                class="billing-mode-card"
                :class="{ selected: billingMode === 'different' }"
              >
                <input
                  v-model="billingMode"
                  type="radio"
                  name="billing-mode"
                  value="different"
                />
                <div
                  class="radio-ring"
                  :class="{ active: billingMode === 'different' }"
                >
                  <i
                    v-if="billingMode === 'different'"
                    class="ri-check-line check-icon"
                  ></i>
                </div>
                <div class="mode-text">
                  <p class="option-title">Use different billing address</p>
                </div>
              </label>
            </div>

            <div v-if="billingMode === 'different'" class="address-list">
              <AddressFormCard
                :show="showAddressForm && addressFormMode === 'billing'"
                :form="addressForm"
                :address-types="addressTypes"
                :error-message="addressError"
                :type-warning-message="addressTypeWarning"
                :pincode-warning-message="pincodeWarningMessage"
                :is-editing="editingAddressId !== null"
                :mode-label="addressFormModeLabel"
                @update-field="updateAddressField"
                @cancel="cancelAddressForm"
                @submit="addAddress"
              />

              <p v-if="!billingCandidateAddresses.length" class="status-text">
                No separate billing address found. Please add billing address.
              </p>

              <article
                v-for="address in visibleBillingAddresses"
                :key="`billing-${address.id}`"
                class="address-card"
                :class="{ selected: selectedBillingAddressId === address.id }"
                @click="selectedBillingAddressId = address.id"
              >
                <div class="addr-card-top">
                  <div class="addr-left-meta">
                    <div class="addr-type-badge">
                      <i :class="getAddressTypeIcon(address.addressType)"></i>
                      {{ address.label || address.addressType || "Home" }}
                    </div>
                  </div>
                  <div class="addr-right-actions">
                    <button
                      type="button"
                      class="icon-action-btn"
                      @click.stop="startEditAddress(address.id, 'billing')"
                      title="Edit Billing Address"
                    >
                      <i class="ri-edit-line"></i>
                    </button>
                    <button
                      type="button"
                      class="icon-action-btn danger"
                      @click.stop="removeAddress(address.id, 'billing')"
                      title="Delete Billing Address"
                    >
                      <i class="ri-delete-bin-line"></i>
                    </button>
                  </div>
                </div>

                <div class="addr-body">
                  <div class="addr-check-col">
                    <div
                      class="radio-ring"
                      :class="{
                        active: selectedBillingAddressId === address.id,
                      }"
                    >
                      <i
                        v-if="selectedBillingAddressId === address.id"
                        class="ri-check-line check-icon"
                      ></i>
                    </div>
                  </div>
                  <div class="addr-text-col">
                    <p class="address-name">
                      {{ address.fullName || "Address" }}
                    </p>
                    <p class="address-line">
                      {{ address.addressLine || "Address line" }}
                    </p>
                    <p class="address-meta">
                      {{
                        [address.city, address.state, address.pincode]
                          .filter(Boolean)
                          .join(", ")
                      }}
                    </p>
                  </div>
                </div>
              </article>
            </div>
          </div>

          <div class="gstin-field-wrap">
            <label for="place-order-gstin" class="gstin-label"
              >GSTIN (Optional)</label
            >
            <input
              id="place-order-gstin"
              type="text"
              class="gstin-input"
              :value="customerGSTNumber"
              maxlength="15"
              placeholder="Enter GSTIN"
              autocomplete="off"
              @input="onCustomerGstinInput"
            />
            <p v-if="gstinValidationMessage" class="small-error">
              {{ gstinValidationMessage }}
            </p>
          </div>

          <p v-if="deliveryAddressError" class="status-text error">
            {{ deliveryAddressError }}
          </p>
        </section>

        <!-- RIGHT: ORDER SUMMARY + RESPONSE -->
        <section class="right-col">
          <!-- ORDER SUMMARY -->
          <div class="summary-card">
            <h2 class="col-title">Order Summary</h2>

            <div class="summary-items">
              <div
                v-for="(item, index) in checkoutItems"
                :key="item.key || index"
                class="summary-item"
              >
                <!-- Clean image, no floating badge -->
                <div class="summary-item-img">
                  <img v-if="item.image" :src="item.image" :alt="item.name" />
                  <div v-else class="img-placeholder">
                    <i class="ri-image-line"></i>
                  </div>
                </div>
                <div class="summary-item-info">
                  <p class="summary-item-name">{{ item.name }}</p>
                  <p class="summary-item-sku" v-if="item.sku">
                    SKU: {{ item.sku }}
                  </p>
                  <!-- Qty as inline text, not floating badge -->
                  <p class="summary-item-qty">Qty: {{ item.quantity || 1 }}</p>
                  <p
                    class="summary-item-qty"
                    v-if="
                      showCv &&
                      item.coinValue !== undefined &&
                      item.coinValue !== null
                    "
                  >
                    CV: {{ formatNumber(item.coinValue) }}
                  </p>
                </div>
                <!-- <p class="summary-item-price">
                  {{ formatRs(getLineTotal(item)) }}
                </p> -->
              </div>
            </div>

            <div class="summary-totals">
              <div class="total-row">
                <span>Gross Total</span>
                <span>{{ formatRs(orderGrossTotal) }}</span>
              </div>
              <div
                v-for="combo in comboSummary.combos"
                :key="combo.comboId"
                class="total-row combo-discount-row"
              >
                <span>
                  <i class="ri-price-tag-3-fill"></i>
                  Combo Discount &middot; {{ combo.name }}
                </span>
                <span>-{{ formatRs(combo.amount) }}</span>
              </div>
              <div class="total-row">
                <span>Subtotal</span>
                <span>{{ formatRs(orderSubtotal) }}</span>
              </div>
              <div class="total-row">
                <span>Shipping</span>
                <span>{{ formatRs(orderShippingTotal) }}</span>
              </div>
              <div class="total-row" v-if="showCv">
                <span>Total CV</span>
                <span>{{ formatNumber(orderTotalCoinValue) }}</span>
              </div>
              <div class="total-row delivery-row">
                <span class="delivery-estimate">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect x="1" y="3" width="15" height="13"></rect>
                    <polygon
                      points="16 8 20 8 23 11 23 16 16 16 16 8"
                    ></polygon>
                    <circle cx="5.5" cy="18.5" r="2.5"></circle>
                    <circle cx="18.5" cy="18.5" r="2.5"></circle>
                  </svg>
                  Estimated Delivery: <strong>25 - 30 Days</strong>
                </span>
              </div>
              <div class="summary-divider"></div>
              <div class="total-row grand">
                <span>Order Total (Incl. of taxes)</span>
                <strong>{{ formatRs(orderTotalDisplay) }}</strong>
              </div>
            </div>
          </div>

          <!-- CTA -->
          <div class="cta-group">
            <router-link to="/cart" class="cta-ghost">
              <i class="ri-arrow-left-line"></i> Back to Cart
            </router-link>
            <button
              type="button"
              class="cta-primary"
              :disabled="
                isAddressLoading ||
                !canContinueToPayment ||
                isPlacingOrder ||
                Boolean(gstinValidationMessage)
              "
              @click="goToPaymentOptions"
            >
              <i class="ri-lock-line"></i>
              {{ isPlacingOrder ? "Creating Order..." : "Continue to Payment" }}
            </button>
          </div>
        </section>
      </section>
    </main>

    <Footer />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { getCustomerAddress, getToken, validatePinCode } from "rdep-ecom-sdk";
import { getCartComboSummary } from "@/data/comboProducts.js";
import { useAppStore } from "../../stores/useAppStore.js";
import { useCartStore } from "../../stores/useCartStore.js";
import AddressFormCard from "./AddressFormCard.vue";
import AppNavbar from "../AppNavbar.vue";
import Footer from "../FooterView.vue";

function normalizeRoleToken(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const userRoleToken = computed(() => {
  try {
    const rawAuth = window.localStorage.getItem("auth.currentUser");
    const authPayload = rawAuth ? JSON.parse(rawAuth) : {};
    const user = authPayload?.data || authPayload || {};
    const roleCandidates = [
      user?.userType,
      user?.role,
      user?.loginRole,
      user?.customerType,
    ]
      .map(normalizeRoleToken)
      .filter(Boolean);

    return roleCandidates[0] || "";
  } catch (error) {
    return "";
  }
});

const showCv = computed(() => userRoleToken.value === "DISTRIBUTOR");

const cartStore = useCartStore();
const appStore = useAppStore();
// const route = useRoute();
const router = useRouter();

const isLoading = ref(false);
const isAddressLoading = ref(false);
const errorMessage = ref("");
const deliveryAddressError = ref("");

const showAddressForm = ref(false);
const addressError = ref("");
const pincodeWarningMessage = ref("");
const addressIdCounter = ref(2);
const editingAddressId = ref(null);
const addressFormMode = ref("shipping");
const isSavingAddress = ref(false);
const isPlacingOrder = ref(false);
const pincodeValidationRequestId = ref(0);

const addressTypes = [
  { value: "Home", label: "Home", icon: "ri-home-4-line" },
  { value: "Office", label: "Office", icon: "ri-building-line" },
  { value: "Other", label: "Other", icon: "ri-map-pin-line" },
];

function getAddressTypeIcon(type) {
  const found = addressTypes.find((t) => t.value === type);
  return found ? found.icon : "ri-map-pin-line";
}

const addresses = ref([]);
const selectedAddressId = ref(null);
const selectedBillingAddressId = ref(null);
const billingMode = ref("same");
const showAllShippingAddresses = ref(false);
const showAllBillingAddresses = ref(false);
const customerGSTNumber = ref("");

function normalizeCustomerGstNumber(value) {
  return String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 15);
}

function isValidGstin(value) {
  const gstin = String(value || "")
    .trim()
    .toUpperCase();
  if (!gstin) return true;
  return /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(gstin);
}

const gstinValidationMessage = computed(() => {
  const gstin = String(customerGSTNumber.value || "").trim();
  if (!gstin) return "";
  if (gstin.length < 15) return "GSTIN must be 15 characters.";
  return isValidGstin(gstin) ? "" : "Please enter a valid GSTIN.";
});

function onCustomerGstinInput(event) {
  customerGSTNumber.value = normalizeCustomerGstNumber(
    event?.target?.value || "",
  );
}

const defaultShippingAddress = computed(() => {
  if (!addresses.value.length) return null;
  return addresses.value.find((entry) => entry.isDefault) || addresses.value[0];
});

const hasOtherShippingAddresses = computed(() => addresses.value.length > 1);

const visibleShippingAddresses = computed(() => {
  if (showAllShippingAddresses.value) return addresses.value;

  const selectedAddress = addresses.value.find(
    (entry) => entry.id === selectedAddressId.value,
  );

  if (selectedAddress) {
    return [selectedAddress];
  }

  return defaultShippingAddress.value ? [defaultShippingAddress.value] : [];
});

function toggleShippingAddressList() {
  showAllShippingAddresses.value = !showAllShippingAddresses.value;
}

const canContinueToPayment = computed(() => {
  if (!addresses.value.length) return false;
  const hasShipping = addresses.value.some(
    (entry) => entry.id === selectedAddressId.value,
  );
  if (!hasShipping) return false;

  if (billingMode.value === "same") return true;

  return addresses.value.some(
    (entry) => entry.id === selectedBillingAddressId.value,
  );
});

const billingCandidateAddresses = computed(() =>
  addresses.value.filter((entry) => entry.id !== selectedAddressId.value),
);

const hasOtherBillingAddresses = computed(
  () => billingCandidateAddresses.value.length > 1,
);

const visibleBillingAddresses = computed(() => {
  if (showAllBillingAddresses.value) return billingCandidateAddresses.value;

  const selectedBilling = billingCandidateAddresses.value.find(
    (entry) => entry.id === selectedBillingAddressId.value,
  );

  if (selectedBilling) {
    return [selectedBilling];
  }

  return billingCandidateAddresses.value.length
    ? [billingCandidateAddresses.value[0]]
    : [];
});

function toggleBillingAddressList() {
  showAllBillingAddresses.value = !showAllBillingAddresses.value;
}

const addressFormModeLabel = computed(() => {
  if (addressFormMode.value === "billing") {
    return editingAddressId.value !== null
      ? "Edit Billing Address"
      : "Add Billing Address";
  }

  return editingAddressId.value !== null
    ? "Edit Delivery Address"
    : "Add New Delivery Address";
});

const addressForm = ref({
  addressType: "Home",
  otherLabel: "",
  fullName: "",
  phone: "",
  //   email: "",
  addressLine: "",
  city: "",
  state: "",
  pincode: "",
});

function normalizeLabel(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function getAddressLabelValidationMessage({ includeRequired = true } = {}) {
  const selectedType = String(addressForm.value.addressType || "Home").trim();
  const normalizedSelectedType = normalizeLabel(selectedType);
  const editingId = editingAddressId.value;

  const existingAddresses = addresses.value.filter(
    (entry) => entry.id !== editingId,
  );

  if (
    normalizedSelectedType === "home" ||
    normalizedSelectedType === "office"
  ) {
    const hasDuplicateType = existingAddresses.some(
      (entry) => normalizeLabel(entry.addressType) === normalizedSelectedType,
    );
    if (hasDuplicateType) {
      return `${selectedType} is already selected for another address. Please choose a different label.`;
    }
    return "";
  }

  if (normalizedSelectedType === "other") {
    const customLabel = String(addressForm.value.otherLabel || "").trim();
    if (!customLabel) {
      return includeRequired ? "Please enter a label for Other address." : "";
    }

    const normalizedCustomLabel = normalizeLabel(customLabel);
    const hasDuplicateLabel = existingAddresses.some(
      (entry) =>
        normalizeLabel(entry.label || entry.addressType) ===
        normalizedCustomLabel,
    );

    if (hasDuplicateLabel) {
      return "This address label already exists. Please use a different label.";
    }
  }

  return "";
}

const addressTypeWarning = computed(() => {
  if (!showAddressForm.value) return "";
  return getAddressLabelValidationMessage({ includeRequired: false });
});

const checkoutItems = ref([]);
const checkoutCartId = ref("");

function normalizeAddressesFromResponse(response) {
  const root = response?.data || response || {};
  const nested = root?.data || {};

  const list =
    (Array.isArray(root) ? root : null) ||
    root.addresses ||
    root.addressList ||
    root.customerAddresses ||
    root.items ||
    nested.addresses ||
    nested.addressList ||
    nested.customerAddresses ||
    nested.items ||
    [];

  if (!Array.isArray(list)) return [];

  return list.map((entry, index) => {
    const rawId = entry?.id ?? entry?.addressId ?? entry?.customerAddressId;
    const parsedId = Number(rawId);
    const nextId =
      Number.isFinite(parsedId) && parsedId > 0 ? parsedId : index + 1;

    const typeRaw = String(
      entry?.tag || entry?.addressType || entry?.type || "Home",
    ).trim();
    const addressType =
      typeRaw.toLowerCase() === "office"
        ? "Office"
        : typeRaw.toLowerCase() === "other"
        ? "Other"
        : "Home";

    const addressLabel = String(
      entry?.label || entry?.tag || addressType,
    ).trim();

    const isDefaultRaw =
      entry?.isDefault ??
      entry?.defaultDeliveryAddress ??
      entry?.defaultAddress ??
      entry?.isDefaultAddress ??
      entry?.default_delivery_address ??
      entry?.is_default ??
      entry?.default;

    const addressLine =
      entry?.addressLine ||
      entry?.address ||
      [entry?.addressLine1, entry?.addressLine2, entry?.addressLine3]
        .filter(Boolean)
        .join(", ") ||
      entry?.address1 ||
      entry?.line1 ||
      "";

    const isDefault =
      isDefaultRaw === true ||
      isDefaultRaw === 1 ||
      isDefaultRaw === "1" ||
      String(isDefaultRaw || "").toUpperCase() === "Y" ||
      String(isDefaultRaw || "").toLowerCase() === "true";

    return {
      id: nextId,
      label: addressLabel,
      tag: entry?.tag || addressType,
      addressType,
      fullName:
        entry?.fullName ||
        entry?.name ||
        entry?.customerName ||
        entry?.contactPerson ||
        "",
      phone:
        entry?.phone ||
        entry?.mobile ||
        entry?.mobileNumber ||
        entry?.customerMobile ||
        entry?.customerMobileNumber ||
        entry?.contactPhoneNumber ||
        "",
      //   email: entry?.email || entry?.mailId || entry?.customerEmail || "",
      contactPhoneNumber:
        entry?.contactPhoneNumber ||
        entry?.phone ||
        entry?.mobile ||
        entry?.mobileNumber ||
        "",
      addressLine1:
        entry?.addressLine1 || entry?.address1 || entry?.line1 || "",
      addressLine2:
        entry?.addressLine2 || entry?.address2 || entry?.line2 || "",
      addressLine3:
        entry?.addressLine3 || entry?.address3 || entry?.line3 || "",
      addressLine,
      city: entry?.city || entry?.district || "",
      district: entry?.district || entry?.city || "",
      state: entry?.state || "",
      country: entry?.country || "India",
      landmark: entry?.landmark || "",
      locationUrl: entry?.locationUrl || "",
      pincode:
        entry?.pincode ||
        entry?.pinCode ||
        entry?.postalCode ||
        entry?.zipCode ||
        "",
      isDefault,
      fromApi: true,
    };
  });
}

async function loadCustomerAddresses() {
  isAddressLoading.value = true;
  try {
    const response = await getCustomerAddress();
    const apiAddresses = normalizeAddressesFromResponse(response);

    if (apiAddresses.length) {
      addresses.value = apiAddresses;
      ensureOneDefaultAddress();
      showAddressForm.value = false;
      addressError.value = "";

      const selected =
        addresses.value.find((entry) => entry.isDefault) || addresses.value[0];
      selectedAddressId.value = selected.id;
      if (billingMode.value === "same") {
        selectedBillingAddressId.value = selected.id;
      } else {
        const firstBilling =
          addresses.value.find((entry) => entry.id !== selected.id) ||
          addresses.value[0];
        selectedBillingAddressId.value = firstBilling?.id || selected.id;
      }
      deliveryAddressError.value = "";

      const maxId = addresses.value.reduce((max, entry) => {
        const idNum = Number(entry.id);
        return Number.isFinite(idNum) ? Math.max(max, idNum) : max;
      }, 0);
      addressIdCounter.value = maxId + 1;
    } else {
      addresses.value = [];
      selectedAddressId.value = null;
      showAddressForm.value = true;
      deliveryAddressError.value = "";
    }
  } catch (error) {
    // Keep local address flow available even if API fails.
    if (!addresses.value.length) {
      showAddressForm.value = true;
    }
  } finally {
    isAddressLoading.value = false;
  }
}

async function deleteCustomerAddressById(addressId) {
  const encodedAddressId = encodeURIComponent(String(addressId));
  const endpoint = `/customer-service/cws/address/${encodedAddressId}`;
  const baseURL = String(appStore.tenantApiBaseUrl || appStore.baseUrl || "");
  const token = getToken();

  const response = await fetch(`${baseURL}${endpoint}`, {
    method: "DELETE",
    headers: {
      ...(token
        ? {
            Authorization: token.startsWith("Bearer ")
              ? token
              : `Bearer ${token}`,
          }
        : {}),
    },
  });

  if (!response.ok) {
    let message = "Failed to remove address.";
    try {
      const payload = await response.json();
      message = payload?.statusMessage || payload?.message || message;
    } catch (error) {
      // keep fallback error message
    }
    throw new Error(message);
  }
}

function buildAddressPayload(
  formValue,
  { isDefault = false, existingAddress = null } = {},
) {
  const type = String(formValue?.addressType || "Home").trim();
  const limitAddressLine = (value) =>
    String(value || "")
      .split(/\r?\n/)
      .map((line) => line.slice(0, 110))
      .join("\n");
  const addressLine2 = limitAddressLine(existingAddress?.addressLine2);
  const addressLine3 = limitAddressLine(existingAddress?.addressLine3);
  const district = existingAddress?.district || formValue?.city || "";
  const country = existingAddress?.country || "India";
  const landmark = existingAddress?.landmark || "";
  const locationUrl = existingAddress?.locationUrl || "";
  const customOtherLabel = String(formValue?.otherLabel || "").trim();
  const label =
    type.toLowerCase() === "other"
      ? customOtherLabel ||
        String(existingAddress?.label || "").trim() ||
        "Other"
      : type;

  return {
    contactPerson: formValue?.fullName || "",
    contactPhoneNumber:
      formValue?.phone || existingAddress?.contactPhoneNumber || "",
    addressLine1: limitAddressLine(formValue?.addressLine),
    addressLine2,
    addressLine3,
    city: formValue?.city || "",
    district,
    state: formValue?.state || "",
    country,
    pinCode: formValue?.pincode || "",
    defaultDeliveryAddress: Boolean(isDefault),
    tag: type,
    landmark,
    label,
    locationUrl,
  };
}

function extractAddressIdFromResponse(response) {
  const root = response?.data || response || {};
  const nested = root?.data || {};
  const rawId =
    root?.id ||
    root?.addressId ||
    root?.customerAddressId ||
    nested?.id ||
    nested?.addressId ||
    nested?.customerAddressId;
  const parsedId = Number(rawId);
  return Number.isFinite(parsedId) ? parsedId : null;
}

async function saveCustomerAddress({
  addressId = null,
  formValue,
  isDefault,
  existingAddress = null,
}) {
  const hasAddressId = addressId !== null && addressId !== undefined;
  const encodedAddressId = hasAddressId
    ? encodeURIComponent(String(addressId))
    : "";
  const endpoint = hasAddressId
    ? `/customer-service/cws/address/${encodedAddressId}`
    : "/customer-service/cws/address";
  const method = hasAddressId ? "PATCH" : "POST";
  const baseURL = String(appStore.tenantApiBaseUrl || appStore.baseUrl || "");
  const token = getToken();
  const payload = buildAddressPayload(formValue, {
    isDefault,
    existingAddress,
  });

  const response = await fetch(`${baseURL}${endpoint}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token
        ? {
            Authorization: token.startsWith("Bearer ")
              ? token
              : `Bearer ${token}`,
          }
        : {}),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = hasAddressId
      ? "Failed to update address."
      : "Failed to add address.";
    try {
      const errorPayload = await response.json();
      message = errorPayload?.statusMessage || errorPayload?.message || message;
    } catch (error) {
      // keep fallback error message
    }
    throw new Error(message);
  }

  try {
    return await response.json();
  } catch (error) {
    return null;
  }
}

function normalizeItemsFromResponse(response) {
  const data = response?.data || response || {};
  const nested = data?.data || {};
  const list =
    data.cartItems ||
    data.items ||
    data.item_list ||
    data.cart?.item_list ||
    nested.cartItems ||
    nested.items ||
    nested.item_list ||
    nested.cart?.item_list ||
    [];
  if (!Array.isArray(list)) return [];
  return list.map((item, index) => ({
    key: item.productId || item.id || item.sku || index,
    name: item.name || item.product?.name || "Product",
    sku: item.sku || item.product?.sku || "",
    quantity: Number(item.quantity || item.qty || item.itemQty || 1) || 1,
    price:
      Number(
        item.netPrice ??
          item.net_price ??
          item.price ??
          item.listPrice ??
          item.mrp ??
          0,
      ) || 0,
    mrp: Number(item.mrp ?? item.price ?? item.listPrice ?? 0) || 0,
    coinValue:
      Number(item.coinValue ?? item.coin_value ?? item.totalCoinValue ?? 0) ||
      0,
    shippingCharge:
      Number(item.shippingCharge ?? item.shipping_charge ?? 0) || 0,
    productId: item.productId || item.id || "",
    storeCode: item.storeCode || item.store_code || item.store?.storeCode || "",
    storeId:
      Number(item.storeId || item.store_id || item.store?.id || 0) || undefined,
  }));
}

const orderSubtotal = computed(() => Number(cartStore.orderNetTotal) || 0);

const comboSummary = computed(() => getCartComboSummary(cartStore.items));

const orderGrossTotal = computed(() => {
  const gross =
    orderSubtotal.value + Number(comboSummary.value.totalDiscount || 0);
  return Math.round((gross + Number.EPSILON) * 100) / 100;
});

const orderShippingTotal = computed(
  () => Number(cartStore.orderShippingCharge) || 0,
);

const orderTotalDisplay = computed(
  () => Number(cartStore.displayOrderTotal) || 0,
);

const orderTotalCoinValue = computed(() => {
  const totalCoinValue = Number(cartStore.cartMeta?.totalCoinValue);
  return Number.isFinite(totalCoinValue)
    ? totalCoinValue
    : Number(cartStore.grandTotalCoinValue) || 0;
});

function ensureOneDefaultAddress() {
  if (!addresses.value.length) return;
  const current = addresses.value.find((e) => e.isDefault);
  if (!current) {
    addresses.value = addresses.value.map((e, i) => ({
      ...e,
      isDefault: i === 0,
    }));
    return;
  }
  let used = false;
  addresses.value = addresses.value.map((e) => {
    if (e.id === current.id && !used) {
      used = true;
      return { ...e, isDefault: true };
    }
    return { ...e, isDefault: false };
  });
}

function parsePincodeValidationResult(response) {
  if (typeof response === "boolean") return response;

  const root = response?.data || response || {};
  if (typeof root === "boolean") return root;

  const candidates = [
    root?.applicable,
    root?.isApplicable,
    root?.valid,
    root?.isValid,
    root?.serviceable,
    root?.isServiceable,
    root?.status,
    root?.result,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "boolean") return candidate;
  }

  return null;
}

async function validateAddressPincode(pincodeValue) {
  const normalized = String(pincodeValue || "")
    .replace(/\D/g, "")
    .slice(0, 6);

  if (normalized.length !== 6) {
    pincodeWarningMessage.value = "";
    return;
  }

  const currentRequestId = pincodeValidationRequestId.value + 1;
  pincodeValidationRequestId.value = currentRequestId;

  try {
    const response = await validatePinCode(normalized);
    if (currentRequestId !== pincodeValidationRequestId.value) return;

    const isApplicable = parsePincodeValidationResult(response);
    pincodeWarningMessage.value =
      isApplicable === false
        ? "Your area is currently unserviceable. Please check again later."
        : "";
  } catch (error) {
    if (currentRequestId !== pincodeValidationRequestId.value) return;
    pincodeWarningMessage.value = "";
  }
}

function openAddressForm(mode = "shipping") {
  if (showAddressForm.value && addressFormMode.value === mode) {
    cancelAddressForm();
    return;
  }

  addressFormMode.value = mode;
  editingAddressId.value = null;
  showAddressForm.value = true;
  addressError.value = "";
  pincodeWarningMessage.value = "";
  addressForm.value = {
    addressType: "Home",
    otherLabel: "",
    fullName: "",
    phone: "",
    // email: "",
    addressLine: "",
    city: "",
    state: "",
    pincode: "",
  };
}

function cancelAddressForm() {
  showAddressForm.value = false;
  addressError.value = "";
  pincodeWarningMessage.value = "";
  editingAddressId.value = null;
  addressFormMode.value = "shipping";
  addressForm.value = {
    addressType: "Home",
    otherLabel: "",
    fullName: "",
    phone: "",
    // email: "",
    addressLine: "",
    city: "",
    state: "",
    pincode: "",
  };
}

function updateAddressField(field, value) {
  if (field === "addressType") {
    addressError.value = "";
    addressForm.value = {
      ...addressForm.value,
      addressType: value,
      otherLabel: value === "Other" ? addressForm.value.otherLabel : "",
    };
    return;
  }

  if (field === "otherLabel") {
    addressError.value = "";
  }

  if (field === "pincode") {
    const normalizedPincode = String(value || "")
      .replace(/\D/g, "")
      .slice(0, 6);
    addressForm.value = {
      ...addressForm.value,
      pincode: normalizedPincode,
    };
    validateAddressPincode(normalizedPincode);
    return;
  }

  addressForm.value = {
    ...addressForm.value,
    [field]: value,
  };
}

function selectAddress(id) {
  selectedAddressId.value = id;
  if (billingMode.value === "same") {
    selectedBillingAddressId.value = id;
  } else if (selectedBillingAddressId.value === id) {
    const nextBilling =
      addresses.value.find((entry) => entry.id !== id) || addresses.value[0];
    selectedBillingAddressId.value = nextBilling?.id || id;
  }
  deliveryAddressError.value = "";
}

watch(
  [billingMode, billingCandidateAddresses, selectedAddressId],
  ([mode, candidates, shippingId]) => {
    if (mode !== "different") {
      showAllBillingAddresses.value = false;
      selectedBillingAddressId.value = shippingId;
      if (showAddressForm.value && addressFormMode.value === "billing") {
        cancelAddressForm();
      }
      return;
    }

    if (Array.isArray(candidates) && candidates.length > 0) {
      if (
        !candidates.some((entry) => entry.id === selectedBillingAddressId.value)
      ) {
        selectedBillingAddressId.value = candidates[0].id;
      }
      return;
    }

    selectedBillingAddressId.value = null;
    if (!(showAddressForm.value && addressFormMode.value === "billing")) {
      openAddressForm("billing");
    }
  },
  { immediate: true },
);

function startEditAddress(id, mode = "shipping") {
  const existing = addresses.value.find((entry) => entry.id === id);
  if (!existing) return;

  addressFormMode.value = mode;
  editingAddressId.value = id;
  if (mode === "shipping") {
    selectedAddressId.value = id;
  } else {
    selectedBillingAddressId.value = id;
  }
  addressError.value = "";
  pincodeWarningMessage.value = "";
  showAddressForm.value = true;
  const existingLabel = String(existing.label || "").trim();
  const isStandardLabel = ["home", "office", "other"].includes(
    normalizeLabel(existingLabel),
  );
  addressForm.value = {
    addressType: existing.addressType || "Home",
    otherLabel:
      existing.addressType === "Other" && !isStandardLabel ? existingLabel : "",
    fullName: existing.fullName || "",
    phone: existing.phone || "",
    // email: existing.email || "",
    addressLine: existing.addressLine || "",
    city: existing.city || "",
    state: existing.state || "",
    pincode: existing.pincode || "",
  };
}

async function addAddress() {
  addressError.value = "";
  if (isSavingAddress.value) return;

  if (pincodeWarningMessage.value) {
    addressError.value = pincodeWarningMessage.value;
    return;
  }

  const labelValidationMessage = getAddressLabelValidationMessage();
  if (labelValidationMessage) {
    addressError.value = labelValidationMessage;
    return;
  }

  const f = addressForm.value;
  if (
    !f.fullName ||
    !f.phone ||
    // !f.email ||
    !f.addressLine ||
    !f.city ||
    !f.state ||
    !f.pincode
  ) {
    addressError.value = "Please fill all address fields.";
    return;
  }

  isSavingAddress.value = true;

  if (editingAddressId.value !== null) {
    const targetId = editingAddressId.value;
    const existingAddress =
      addresses.value.find((entry) => entry.id === targetId) || null;
    const isDefault =
      addresses.value.find((entry) => entry.id === targetId)?.isDefault ||
      false;
    try {
      await saveCustomerAddress({
        addressId: targetId,
        formValue: f,
        isDefault,
        existingAddress,
      });
      await loadCustomerAddresses();
      if (
        addressFormMode.value === "billing" &&
        billingMode.value === "different"
      ) {
        selectedBillingAddressId.value = targetId;
      } else {
        selectedAddressId.value = targetId;
      }
      cancelAddressForm();
    } catch (error) {
      addressError.value = error?.message || "Failed to update address.";
    } finally {
      isSavingAddress.value = false;
    }
    return;
  }

  const hasDefault = addresses.value.some((e) => e.isDefault);
  try {
    const saveResponse = await saveCustomerAddress({
      formValue: f,
      isDefault: !hasDefault,
    });
    const createdId = extractAddressIdFromResponse(saveResponse);

    await loadCustomerAddresses();
    if (createdId !== null) {
      const created = addresses.value.find((entry) => entry.id === createdId);
      if (created) {
        if (
          addressFormMode.value === "billing" &&
          billingMode.value === "different"
        ) {
          selectedBillingAddressId.value = created.id;
        } else {
          selectedAddressId.value = created.id;
        }
      }
    }
    cancelAddressForm();
  } catch (error) {
    addressError.value = error?.message || "Failed to add address.";
  } finally {
    isSavingAddress.value = false;
  }
}

async function setDefaultAddress(addressOrId) {
  const targetId =
    typeof addressOrId === "object" && addressOrId !== null
      ? addressOrId.id
      : addressOrId;

  const targetAddress =
    typeof addressOrId === "object" && addressOrId !== null
      ? addressOrId
      : addresses.value.find((e) => e.id === targetId);

  if (!targetAddress) return;

  addresses.value = addresses.value.map((e) => ({
    ...e,
    isDefault: e.id === targetId,
  }));
  selectAddress(targetId);

  if (!targetAddress.fromApi) return;

  try {
    await saveCustomerAddress({
      addressId: targetId,
      formValue: targetAddress,
      isDefault: true,
      existingAddress: targetAddress,
    });
    await loadCustomerAddresses();

    const refreshedTarget = addresses.value.find((e) => e.id === targetId);
    if (refreshedTarget) {
      if (!refreshedTarget.isDefault) {
        addresses.value = addresses.value.map((e) => ({
          ...e,
          isDefault: e.id === targetId,
        }));
      }
      selectAddress(targetId);
    }
  } catch (error) {
    addressError.value = error?.message || "Failed to update default address.";
  }
}

async function removeAddress(id, mode = "shipping") {
  addressError.value = "";

  const existing = addresses.value.find((entry) => entry.id === id);
  if (!existing) return;

  if (existing.fromApi) {
    try {
      await deleteCustomerAddressById(id);
      await loadCustomerAddresses();
    } catch (error) {
      addressError.value = error?.message || "Failed to remove address.";
      return;
    }
    return;
  }

  addresses.value = addresses.value.filter((e) => e.id !== id);

  if (!addresses.value.length) {
    selectedAddressId.value = null;
    selectedBillingAddressId.value = null;
    openAddressForm(mode);
    return;
  }

  ensureOneDefaultAddress();
  if (!addresses.value.some((e) => e.id === selectedAddressId.value)) {
    const fallback =
      addresses.value.find((e) => e.isDefault) || addresses.value[0];
    selectedAddressId.value = fallback?.id ?? null;
  }

  if (billingMode.value === "same") {
    selectedBillingAddressId.value = selectedAddressId.value;
  } else if (
    !addresses.value.some((e) => e.id === selectedBillingAddressId.value)
  ) {
    const billingFallback =
      addresses.value.find((e) => e.id !== selectedAddressId.value) ||
      addresses.value[0];
    selectedBillingAddressId.value =
      billingFallback?.id ?? selectedAddressId.value;
  }

  if (selectedAddressId.value !== null) {
    deliveryAddressError.value = "";
  }
}

async function goToPaymentOptions() {
  if (!addresses.value.length) {
    deliveryAddressError.value =
      "Please add a delivery address before continuing to payment.";
    showAddressForm.value = true;
    return;
  }

  if (!canContinueToPayment.value) {
    deliveryAddressError.value =
      billingMode.value === "different"
        ? "Please select a billing address before continuing to payment."
        : "Please select a delivery address before continuing to payment.";
    return;
  }

  if (gstinValidationMessage.value) {
    deliveryAddressError.value =
      "Please enter a valid GSTIN or clear the field.";
    return;
  }

  if (isPlacingOrder.value) return;

  isPlacingOrder.value = true;
  deliveryAddressError.value = "";
  errorMessage.value = "";

  try {
    await cartStore.refreshCartData();

    await router.push({
      name: "payment-options",
      query: {
        shippingAddressId: String(selectedAddressId.value || "").trim(),
        billingAddressId: String(
          (billingMode.value === "same"
            ? selectedAddressId.value
            : selectedBillingAddressId.value) || "",
        ).trim(),
        customerGSTNumber: normalizeCustomerGstNumber(customerGSTNumber.value),
      },
    });
  } catch (error) {
    deliveryAddressError.value =
      error?.response?.data?.statusMessage ||
      error?.message ||
      "Failed to create order. Please try again.";
  } finally {
    isPlacingOrder.value = false;
  }
}

function formatRs(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "₹0";
  return `₹${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(parsed)}`;
}

function formatNumber(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "0";
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(parsed);
}

// function getLineTotal(item) {
//   return (Number(item?.quantity || 1) || 1) * (Number(item?.price || 0) || 0);
// }

async function loadCheckoutData() {
  isLoading.value = true;
  errorMessage.value = "";
  try {
    await cartStore.syncGuestCartAfterLogin();
    await cartStore.refreshCartData();
    const cartId = Number(cartStore.cartMeta?.cartId || 0);
    if (!cartId) {
      checkoutItems.value = [...(cartStore.items || [])];
      throw new Error("Cart not found. Please add products and try again.");
    }
    checkoutCartId.value = cartId;
    const response = await cartStore.refreshCartData();
    const apiItems = normalizeItemsFromResponse(response);
    checkoutItems.value = apiItems.length
      ? apiItems
      : [...(cartStore.items || [])];
  } catch (error) {
    checkoutItems.value = [...(cartStore.items || [])];
    errorMessage.value =
      error?.response?.data?.statusMessage ||
      error?.message ||
      "Failed to load checkout details.";
  } finally {
    isLoading.value = false;
  }
}

onMounted(async () => {
  ensureOneDefaultAddress();
  await Promise.all([loadCheckoutData(), loadCustomerAddresses()]);
});
</script>

<style scoped>
@import url("https://cdn.jsdelivr.net/npm/remixicon@4.2.0/fonts/remixicon.css");

.place-order-page {
  background: #f8fafc;
  min-height: 100vh;
}

.container {
  width: 92%;
  max-width: 1200px;
  margin: 0 auto;
}

.place-order-content {
  padding: 32px 0 64px;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

/* ── PROGRESS ── */
.checkout-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 8px 0 4px;
}
.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.step-dot {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #d1dbe8;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 16px;
}
.progress-step.completed .step-dot {
  border-color: #003da5;
  background: #003da5;
  color: #fff;
}
.progress-step.active .step-dot {
  border-color: #003da5;
  background: #fff;
  color: #003da5;
  box-shadow: 0 0 0 4px rgba(0, 61, 165, 0.1);
}
.step-label {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
}
.progress-step.completed .step-label,
.progress-step.active .step-label {
  color: #003da5;
}
.progress-line {
  width: 80px;
  height: 2px;
  background: #d1dbe8;
  margin-bottom: 18px;
}
.progress-line.active {
  background: #003da5;
}

/* ── GRID ── */
.checkout-grid {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 24px;
  align-items: start;
}
.left-col,
.right-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.col-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.shipping-header-actions {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.col-title {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #0a2240;
  letter-spacing: -0.3px;
}

.more-addr-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1.5px solid #003db8;
  background: #083da5;
  color: #fff;
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.more-addr-btn:hover {
  border-color: #003da5;
  background: #fff;
  color: #083da5;
}

/* ── ADD NEW BTN ── */
.add-addr-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1.5px solid #003da5;
  background: transparent;
  color: #003da5;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}
.add-addr-btn:hover {
  background: #003da5;
  color: #fff;
}

/* ── ADDRESS LIST ── */
.address-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.address-card {
  border: 1.5px solid #e2eaf6;
  border-radius: 14px;
  background: #fff;
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.address-card:hover {
  border-color: #b3c8ee;
}
.address-card.selected {
  border-color: #003da5;
  box-shadow: 0 0 0 3px rgba(0, 61, 165, 0.08);
  background: #fafcff;
}

/* TOP ROW */
.addr-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.addr-left-meta {
  display: flex;
  align-items: center;
  gap: 8px;
}
.addr-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: rgba(0, 61, 165, 0.06);
  color: #003da5;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 700;
}
.default-pill {
  font-size: 11px;
  font-weight: 700;
  color: #0a2240;
  background: #ffc72c;
  border-radius: 999px;
  padding: 3px 9px;
}
.addr-right-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.icon-action-btn {
  width: 30px;
  height: 30px;
  border: 1px solid #e2eaf6;
  background: #f8fafc;
  color: #5a6b82;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: all 0.18s;
  font-size: 15px;
}
.icon-action-btn:hover {
  border-color: #003da5;
  color: #003da5;
  background: rgba(0, 61, 165, 0.05);
}
.icon-action-btn.danger:hover {
  border-color: #dc2626;
  color: #dc2626;
  background: #fff4f4;
}
.icon-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* BODY */
.addr-body {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.addr-check-col {
  flex-shrink: 0;
  padding-top: 2px;
}
.radio-ring {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #d1dbe8;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s;
  background: #fff;
}
.radio-ring.active {
  border-color: #003da5;
  background: #003da5;
}
.check-icon {
  font-size: 12px;
  color: #fff;
  font-weight: 700;
}
.addr-text-col {
  flex: 1;
  min-width: 0;
}
.address-name {
  margin: 0 0 3px;
  font-size: 15px;
  font-weight: 700;
  color: #0a2240;
}
.address-line {
  margin: 0 0 3px;
  font-size: 13px;
  color: #344760;
  line-height: 1.5;
}
.address-meta {
  margin: 0;
  font-size: 13px;
  color: #5a6b82;
}
.set-default-btn {
  margin-top: 8px;
  border: none;
  background: none;
  color: #003da5;
  font-size: 12px;
  font-weight: 700;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
}
.set-default-btn:hover {
  color: #0033a0;
}

.billing-section {
  margin-top: 16px;
}

.gstin-field-wrap {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gstin-label {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #0a2240;
}

.gstin-input {
  width: 100%;
  box-sizing: border-box;
  border: 1.5px solid #d1dbe8;
  border-radius: 10px;
  height: 42px;
  padding: 0 12px;
  font-size: 14px;
  color: #0a2240;
  background: #fff;
  font-family: inherit;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.gstin-input:focus {
  outline: none;
  border-color: #003da5;
  box-shadow: 0 0 0 3px rgba(0, 61, 165, 0.12);
}

.billing-title {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 700;
  color: #0a2240;
}

.billing-mode-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
}

.billing-mode-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border: 1.5px solid #e2eaf6;
  border-radius: 12px;
  padding: 12px 14px;
  background: #fff;
  cursor: pointer;
}

.billing-mode-card input[type="radio"] {
  display: none;
}

.billing-mode-card.selected {
  border-color: #003da5;
  box-shadow: 0 0 0 3px rgba(0, 61, 165, 0.08);
  background: #fafcff;
}

.mode-text {
  flex: 1;
}

.option-title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #0a2240;
}

/* ── SUMMARY CARD ── */
.summary-card {
  border: 1px solid #dce7f6;
  border-radius: 16px;
  background: #fff;
  padding: 20px;
}
.summary-items {
  margin: 14px 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.summary-item {
  display: flex;
  align-items: center;
  gap: 14px;
}
.summary-item-img {
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e2eaf6;
  background: #f8fafc;
}
.summary-item-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.img-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 20px;
}
.summary-item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.summary-item-name {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #0a2240;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  display: block;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.summary-item-sku {
  margin: 0;
  font-size: 11px;
  color: #94a3b8;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.summary-item-qty {
  margin: 0;
  font-size: 12px;
  color: #5a6b82;
  font-weight: 600;
}
.summary-item-price {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #003da5;
  white-space: nowrap;
  flex-shrink: 0;
}
.summary-totals {
  border-top: 1px solid #edf2f7;
  padding-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: #344760;
}
.combo-discount-row {
  color: #15803d;
  font-weight: 600;
}
.combo-discount-row i {
  margin-right: 4px;
}
.delivery-row {
  margin-top: 2px;
}
.delivery-estimate {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: #5f6368;
}
.delivery-estimate svg {
  flex-shrink: 0;
  color: #003da5;
}
.delivery-estimate strong {
  color: #0a2240;
  font-weight: 700;
}
.summary-divider {
  height: 1px;
  background: #edf2f7;
  margin: 4px 0;
}
.total-row.grand {
  padding-top: 10px;
  border-top: 1px solid #edf2f7;
  font-size: 16px;
  font-weight: 800;
  color: #0a2240;
}
.free-tag {
  color: #15803d;
  font-weight: 700;
  font-size: 13px;
}

/* ── CTA ── */
.cta-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.cta-ghost {
  width: 100%;
  height: 44px;
  border: 1.5px solid #d1dbe8;
  border-radius: 50px;
  background: #fff;
  color: #344760;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-decoration: none;
  transition: all 0.2s;
  font-family: inherit;
  box-sizing: border-box;
}
.cta-ghost:hover {
  border-color: #003da5;
  color: #003da5;
}

.cta-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #ffc72c;
  border: 2px solid #ffc72c;
  color: #0a2240;
  border-radius: 50px;
  padding: 13px 28px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  font-family: inherit;
  width: 100%;
  box-sizing: border-box;
}
.cta-primary:hover {
  background: #003da5;
  border-color: #003da5;
  color: #fff;
  box-shadow: 0 4px 16px rgba(0, 61, 165, 0.3);
}

.cta-primary:disabled {
  opacity: 0.65;
  cursor: not-allowed;
  box-shadow: none;
}

/* ── SHARED BUTTONS ── */
.ghost-btn {
  border: 1px solid #d1dbe8;
  background: #fff;
  color: #344760;
  border-radius: 999px;
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
}
.primary-btn {
  border: none;
  background: #003da5;
  color: #fff;
  border-radius: 999px;
  padding: 9px 20px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
}
.primary-btn:hover {
  background: #0033a0;
}
.link-btn {
  border: none;
  background: transparent;
  color: #003da5;
  font-size: 13px;
  font-weight: 700;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
}
.link-btn.danger {
  color: #dc2626;
}
.link-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.small-error {
  margin: 4px 0 0;
  color: #dc2626;
  font-size: 13px;
  font-weight: 600;
}

/* ── RESPONSE BOXES ── */
.response-box {
  border: 1px solid #dce7f6;
  border-radius: 12px;
  padding: 14px;
  background: #fff;
}
.response-box h3 {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 800;
  color: #0a2240;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}
.info-cell {
  border: 1px solid #e4ecf9;
  border-radius: 8px;
  background: #f9fbff;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.info-cell span {
  color: #60748f;
  font-size: 12px;
}
.info-cell strong {
  color: #0a2240;
  font-size: 13px;
}
.items-table-row {
  display: grid;
  grid-template-columns: 2fr 1.3fr 0.8fr 1fr 1fr;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #e8eef8;
  font-size: 12px;
  color: #344760;
}
.items-table-head {
  font-weight: 700;
  color: #0a2240;
  border-bottom: 1px solid #dce7f6;
}
.bill-preview {
  margin: 0 0 8px;
  white-space: pre-wrap;
  word-break: break-word;
  background: #f6f9ff;
  border: 1px solid #dce7f6;
  border-radius: 8px;
  padding: 10px;
  max-height: 180px;
  overflow: auto;
  font-size: 11px;
  color: #243952;
}
.raw-response-box pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  background: #f5f8ff;
  border: 1px solid #dce7f6;
  border-radius: 8px;
  padding: 10px;
  max-height: 260px;
  overflow: auto;
  color: #223853;
  font-size: 12px;
}
.response-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  color: #3f526d;
  padding: 4px 0;
}
.response-row strong {
  color: #0a2240;
}
.status-text {
  margin: 0;
  color: #5f6f85;
  font-size: 14px;
}
.status-text.centered {
  text-align: center;
  padding: 40px 0;
}
.status-text.error {
  color: #dc2626;
  font-weight: 700;
}
.response-hint {
  margin-top: 4px;
}

/* ── RESPONSIVE ── */
@media (max-width: 960px) {
  .checkout-grid {
    grid-template-columns: 1fr;
  }
}
@media (max-width: 600px) {
  .col-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  .shipping-header-actions {
    width: 100%;
    justify-content: flex-start;
  }
  .address-form-grid {
    grid-template-columns: 1fr;
  }
  .info-grid {
    grid-template-columns: 1fr;
  }
  .items-table-row {
    grid-template-columns: 1fr;
    gap: 2px;
  }
  .progress-line {
    width: 48px;
  }
  .col-title {
    font-size: 18px;
  }
  .addr-type-group {
    flex-wrap: wrap;
  }
  .place-order-content {
    padding: 24px 0 48px;
  }
}
</style>

